import React, { useEffect, useState, useContext } from 'react'
import {
    useNavigate,
    useParams,
    useSearchParams,
    useLoaderData,
    useRevalidator,
    useLocation,
} from 'react-router-dom'
import { useAuth } from '../../../../hooks/auth'

import { Purchase } from '../types'

import PurhaseStage from './components/PurchaseStage'
import PurhaseInfo from './components/PurchaseInfo'

import PurchaseTab from '../components/Tabs/PurchaseTab'

import PurchaseBaseInfoTab from './components/PurchaseBaseInfoTab'

import { Structure } from '../../Org/Structure/types'
import { CurrencyList, PerformanceChart } from './types'

import { User } from '../../Org/Users/types'
import { OrganizationInfo } from '../../Org/Home/types'

import { ThemeContext } from '../../../../components/ThemeContext'

import HoverTooltip from '../../../../components/HoverTooltip'

import Footer from './components/Footer'

import PurchaseCardTab from './components/PurchaseCardTab'

type ApiResponseRoleGuid = {
    result: QueryItem[]
}

type QueryItem = {
    guid: string
    name: string
    organization_guid: string
    created_at: string
    updated_at: string
}

const TABS = [
    'Карточка закупки',
    'Документация',
    // 'Документы',
]

const getCurrentDate = (): string => {
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0') // добавляем 1, так как месяцы начинаются с 0
    const day = String(today.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

export const loadPurchase = async ({
    params,
}: any): Promise<Purchase | null> => {
    const purchase_reg_number = params.purchase_reg_number

    // Для create — ничего не грузим
    if (!purchase_reg_number) return null

    const url = `${process.env.REACT_APP_API_URL}/api/backend/v1/purchase/purchase_reg_number/${purchase_reg_number}`

    const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
    })

    if (!response.ok) {
        // пусть отрисуется ErrorPage
        throw new Response('Failed to fetch purchase', {
            status: response.status,
        })
    }

    const data = await response.json()

    // API может вернуть либо объект, либо массив с одним объектом
    const purchase: Purchase | null = Array.isArray(data)
        ? (data[0] ?? null)
        : (data ?? null)

    // Доп. проверка, что это действительно закупка
    if (!purchase || !('registration_number' in purchase)) return null

    return purchase
}

const getStructureList = async (): Promise<Structure[]> => {
    return fetch(
        process.env.REACT_APP_API_URL + '/api/backend/v1/organization_struct',
        {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        }
    )
        .then((response) => {
            if (response.ok) {
                return response.json()
            } else {
                throw new Error('Ошибка запроса на способы закупок')
            }
        })
        .catch((e) => {
            console.error(e)
        })
}

const getRole = async (role_guid: string): Promise<QueryItem> => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/organizations/roles_by_guid/${role_guid}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )

        if (!response.ok) {
            throw new Error('Ошибка запроса на роль')
        }

        const data: ApiResponseRoleGuid = await response.json()
        // Извлекаем первый элемент массива, т.к. он всегда содержит единственный объект
        return data.result[0]
    } catch (error) {
        console.error('Ошибка:', error)
        // Возвращаем объект по умолчанию, соответствующий типу QueryItem
        return {
            guid: '',
            name: '',
            organization_guid: '',
            created_at: '',
            updated_at: '',
        }
    }
}

const getPlanYearFromPurchase = (purchase_data: Purchase): number | null => {
    const sourceDate =
        purchase_data.publication_date_time || purchase_data.sys_created_at
    if (!sourceDate) return null

    const date = new Date(sourceDate)
    if (Number.isNaN(date.getTime())) return null

    return date.getFullYear()
}

const PurchasePage: React.FC = () => {
    const loadedPurchase = useLoaderData() as Purchase

    const navigate = useNavigate()
    const { revalidate } = useRevalidator()

    const [purchase, setPurchase] = useState<Purchase>(loadedPurchase)
    const [orgData, setOrgData] = useState<OrganizationInfo>()

    const { user } = useAuth()
    const { theme } = useContext(ThemeContext)
    const { purchase_reg_number: purchase_reg_number } = useParams()
    const { action: action } = useParams()
    const { tab: tabParam } = useParams()

    const [isUserRoleName, setUserRoleName] = useState('')
    const [structList, setStructList] = useState<Structure[]>([])
    const [currencyList, setCurrencyList] = useState<CurrencyList[]>([])

    const [searchParams] = useSearchParams()
    const initialStage = searchParams.get('tab')

    const [tab, setTab] = useState(initialStage || '')

    const [performanceChart, setPerformanceChart] = useState<PerformanceChart>()

    const [isLoading, setLoading] = useState<boolean>(false)
    const [dataReloadToken, setDataReloadToken] = useState(0)

    // 1) при любом обновлении данных loader — обновляем purchase в state
    useEffect(() => {
        if (!loadedPurchase) return
        setPurchase(loadedPurchase)
    }, [loadedPurchase])

    useEffect(() => {
        if (!purchase) return

        navigate(`/hub/purchase/${purchase.registration_number}?tab=${tab}`)
    }, [tab, purchase?.registration_number, navigate])

    const loadOrganizationData = async (): Promise<OrganizationInfo> => {
        return fetch(
            process.env.REACT_APP_API_URL + '/api/backend/v1/organization',
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then(async (response) => {
                if (response.ok) {
                    const data = await response.json()
                    setOrgData(data)
                    return data
                } else {
                    throw new Error('Failed to fetch organization')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    const getCurrencyList = async (): Promise<CurrencyList[]> => {
        return fetch(
            process.env.REACT_APP_API_URL + '/api/backend/v1/currency',
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка запроса на курсы валют')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    // const loadPerformanceChart = async (): Promise<PerformanceChart> => {
    //     return fetch(
    //         process.env.REACT_APP_API_URL +
    //             `/api/backend/v1/contracts_performance_total_sum_with_currency_by_contract_reg_number?contract_reg_number=${purchase.registration_number}`,
    //         {
    //             method: 'GET',
    //             credentials: 'include',
    //             headers: {
    //                 'Content-Type': 'application/json',
    //             },
    //         }
    //     )
    //         .then(async (response) => {
    //             if (response.ok) {
    //                 const data = await response.json()
    //                 setPerformanceChart(data)
    //                 return data
    //             } else {
    //                 throw new Error('Failed to fetch performance chart')
    //             }
    //         })
    //         .catch((e) => {
    //             console.error(e)
    //         })
    // }

    const updateContractDataFormEis = async (): Promise<void> => {
        setLoading(true)
        const url =
            process.env.REACT_APP_API_URL +
            `/api/backend/v1/purchases/from-eis?reg_number=${purchase.registration_number}`

        try {
            const response = await fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            })

            if (!response.ok) {
                throw new Error(
                    `Failed to update purchase data form EIS. Status: ${response.status}`
                )
            }
            if (tab == 'Карточка договора') {
                revalidate()
            } else if (tab == 'Договор') {
                revalidate()
                setDataReloadToken((x) => x + 1)
            } else if (tab == 'Исполнение договора') {
                revalidate()
                setDataReloadToken((x) => x + 1)
            } else {
                revalidate()
                setDataReloadToken((x) => x + 1)
            }
        } catch (error) {
            console.error('Error while updating purchase data form EIS:', error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                const organization = await loadOrganizationData()
                if (organization) {
                    setOrgData(organization)
                }

                // const performanceChartValue = await loadPerformanceChart()
                // if (performanceChartValue) {
                //     setPerformanceChart(performanceChartValue)
                // }

                const role = await getRole(user?.object.role_id)
                setUserRoleName(role.name)

                const structs = await getStructureList()
                if (structs) {
                    setStructList(structs)
                }

                const currency = await getCurrencyList()
                if (Array.isArray(currency)) {
                    setCurrencyList(currency)
                } else {
                    console.error('Нет данных:', currency)
                }
            } catch (error) {
                console.error('Ошибка при загрузке данных:', error)
            }
        }
        fetchData()
    }, [])

    // useEffect(() => {
    //     if (tab == 'Карточка договора') {
    //         const fetchData = async () => {
    //             try {
    //                 const performanceChartValue = await loadPerformanceChart()
    //                 if (performanceChartValue) {
    //                     setPerformanceChart(performanceChartValue)
    //                 }
    //             } catch (error) {
    //                 console.error('Ошибка при загрузке данных:', error)
    //             }
    //         }
    //         fetchData()
    //     }
    // }, [tab])

    return (
        <div className=" flex flex-col w-full h-screen items-start justify-start ">
            <div className="flex flex-col w-full h-fit items-center justify-center">
                <div className="flex flex-col w-full h-[90px] items-center justify-center bg-[#e5e5e5] dark:bg-[#000000]">
                    <PurhaseStage purchase={purchase} theme={theme} />
                </div>
                <div className="flex flex-col w-full min-h-[100px] items-center justify-center bg-[#ebebeb] dark:bg-[#111111]">
                    <PurhaseInfo
                        purchase={purchase}
                        theme={theme}
                        currencyList={currencyList}
                    />
                </div>
                <div className="flex w-full h-[28px] items-center justify-center bg-[#ebebeb] dark:bg-[#111111]">
                    <PurchaseTab
                        tab={tab}
                        tabs={TABS}
                        onTabChange={(newTab) => {
                            setTab(newTab)
                        }}
                        theme={theme}
                    />
                </div>
            </div>

            <div className="grow flex flex-row w-full h-[calc(100vh_-_218px)] min-h-0 items-center justify-center gap-5 px-5 py-5 bg-[#f0f3f5] dark:bg-[#181818] ">
                <div className="flex flex-col min-w-[1270px] max-w-[1270px] h-full min-h-0 gap-y-12 items-start justify-start overflow-x-visible overflow-y-auto custom-scrollbar">
                    {tab === 'Карточка закупки' && (
                        <fieldset
                            className="flex flex-col w-full"
                            disabled={
                                isUserRoleName != 'admin' &&
                                isUserRoleName != 'specialist'
                            }
                        >
                            <PurchaseCardTab
                                theme={theme}
                                purchase={purchase}
                                purchaseGuid={purchase.guid}
                                annotationPurchase={purchase.annotation}
                                onAnnotationPurchaseChange={(annotation) => {
                                    setPurchase((prev) => ({
                                        ...prev,
                                        annotation: annotation,
                                    }))
                                }}
                                structList={structList} // передаём готовый список
                                performanceChart={
                                    performanceChart ? performanceChart : null
                                }
                                // contractSum={purchase.price}
                                isUserRoleName={isUserRoleName}
                                onSupervisorChange={(
                                    guid,
                                    f_name,
                                    l_name,
                                    p_name
                                ) => {
                                    setPurchase((prev) => ({
                                        ...prev,
                                        supervisor_user_guid: guid,
                                        supervisor_user: {
                                            ...prev.supervisor_user,
                                            first_name: f_name,
                                            last_name: l_name,
                                            patronymic: p_name,
                                        },
                                    }))
                                }}
                            />
                        </fieldset>
                    )}
                    {tab === 'Документация' && (
                        <fieldset
                            className={`flex flex-col w-full`}
                            disabled={false}
                        >
                            <PurchaseBaseInfoTab
                                theme={theme}
                                purchase={purchase}
                                currencyList={currencyList}
                                isActive={tab === 'Документация'}
                                dataReloadToken={dataReloadToken}
                                purchaseYear={getPlanYearFromPurchase(purchase)}
                            />
                        </fieldset>
                    )}
                </div>
            </div>
            <Footer
                theme={theme}
                purchaseGuid={purchase.guid}
                purchaseStage={purchase.purchase_stage}
                user_guid={user?.object.user_id}
                pos_user_guid={purchase.user_guid}
                pos_supervisor_user_guid={purchase.supervisor_user_guid}
                role={isUserRoleName}
                stage={purchase.purchase_stage}
                status={purchase.purchase_status}
                purchaseYear={
                    purchase.sys_created_at
                        ? purchase.sys_created_at.slice(0, 4)
                        : getCurrentDate().slice(0, 4)
                }
                onUpdateContractFormEis={updateContractDataFormEis}
                isLoading={isLoading}
            />
        </div>
    )
}

export default PurchasePage
